import {
  onMounted,
  ref,
  shallowReactive,
  shallowRef,
  watch,
  type ComputedRef,
  type ShallowRef,
} from "vue";
import type { useRuntimeConfig } from "#app";
import { createLogger, useExploreX } from "#imports";
import type { Place } from "~/stores/places";
import type { Spot } from "~/stores/spots";
import { until } from "@vueuse/core";
import mapboxgl from "mapbox-gl";

// configuration
const MAX_ZOOM = 17;
const SPOT_ZOOM = 16;
const PLACE_ZOOM = 13;
const REGION_ZOOM = 11;

type ExploreMap = {
  mapElement: Readonly<
    ShallowRef<HTMLDivElement | null, HTMLDivElement | null>
  >;
  config: ReturnType<typeof useRuntimeConfig>;
  currentPlace: ComputedRef<Place | null>;
  currentSpot: ComputedRef<Spot | null>;
  showSpot: (spot: Spot) => void;
  showPlace: (place: Place) => void;
};

export const useExploreMap = ({
  mapElement,
  config,
  currentPlace,
  currentSpot,
  showPlace,
  showSpot,
}: ExploreMap) => {
  let spotMarkerTemplate: HTMLElement;
  let placeMarkerTemplate: HTMLElement;

  const map = shallowRef<mapboxgl.Map>();
  const isMapLoaded = ref<boolean>(false); // loaded to operate on
  const isMapReady = ref<boolean>(false); // zoomed and panned to the current place

  const exploreStore = useExploreX();
  const logger = createLogger("views/explore");

  // browser render only, no ssr
  onMounted(async () => {
    map.value = new mapboxgl.Map({
      accessToken: config.public.mapboxKey as string,
      container: mapElement.value!,
      style: "mapbox://styles/mapbox/standard",
      maxZoom: MAX_ZOOM,
    });

    // setup template for markers for easy copying
    spotMarkerTemplate = document.createElement("i");
    spotMarkerTemplate.style.display = "inline-block";
    spotMarkerTemplate.style.color = "var(--color-indigo-50)";
    spotMarkerTemplate.style.backgroundColor = "var(--color-indigo-500)";
    spotMarkerTemplate.style.borderRadius = "100%";
    spotMarkerTemplate.style.border = "2px solid var(--color-indigo-600)";
    spotMarkerTemplate.style.width = "10px";
    spotMarkerTemplate.style.height = "10px";
    spotMarkerTemplate.style.cursor = "pointer";

    placeMarkerTemplate = document.createElement("div");

    const placeMarkerDot = document.createElement("div");
    placeMarkerDot.style.backgroundColor = "var(--color-indigo-500)";
    placeMarkerDot.style.width = "8px";
    placeMarkerDot.style.height = "8px";
    placeMarkerDot.style.borderRadius = "100%";
    placeMarkerDot.style.position = "absolute";
    placeMarkerDot.style.top = "50%";
    placeMarkerDot.style.left = "50%";
    placeMarkerDot.style.transform = "translate(-50%, -50%)";
    placeMarkerDot.style.boxShadow = "0 0 0 6px rgba(100, 102, 240, 0.4)";

    const placeMarkerIcon = document.createElement("i");
    placeMarkerIcon.style.position = "absolute";
    placeMarkerIcon.style.top = "50%";
    placeMarkerIcon.style.left = "50%";
    placeMarkerIcon.style.transform = "translate(-50%, -90%)";
    placeMarkerIcon.className = "ri-map-pin-2-fill";

    const placeMarkerCount = document.createElement("span");
    placeMarkerCount.style.position = "absolute";
    placeMarkerCount.style.top = "-0.2em";
    placeMarkerCount.style.left = "50%";
    placeMarkerCount.style.width = "2.15em";
    placeMarkerCount.style.height = "2.15em";
    placeMarkerCount.style.transform = "translate(-50%, -100%)";
    placeMarkerCount.style.backgroundColor = "var(--color-indigo-500)";
    placeMarkerCount.style.color = "white";
    placeMarkerCount.style.display = "flex";
    placeMarkerCount.style.justifyContent = "center";
    placeMarkerCount.style.alignItems = "center";
    placeMarkerCount.style.justifyContent = "center";
    placeMarkerCount.style.borderRadius = "100%";
    placeMarkerCount.style.padding = "10%";
    placeMarkerCount.style.setProperty("font-size", "0.3em", "important");

    placeMarkerDot.appendChild(placeMarkerIcon);
    placeMarkerDot.appendChild(placeMarkerCount);
    placeMarkerTemplate.appendChild(placeMarkerDot);

    placeMarkerTemplate.style.display = "flex";
    placeMarkerTemplate.style.borderRadius = "100%";
    placeMarkerTemplate.style.boxShadow =
      "inset 0 0 0 50px rgba(100, 102, 240, 0.1), inset 0 0 0 1px rgba(100, 102, 240, 0.2)";
    placeMarkerTemplate.style.color = "white";
    placeMarkerTemplate.style.cursor = "pointer";

    const setLocation = () => {
      const bounds = map.value!.getBounds();

      exploreStore.setLatLng({
        zoom: map.value!.getZoom(),
        min: bounds!.getSouthWest(),
        max: bounds!.getNorthEast(),
      });
    };
    map.value!.on("move", setLocation);
    map.value!.on("zoom", setLocation);

    isMapLoaded.value = true;
  });

  // drawnPlaces[place.id] = mapMarker;
  const drawnPlaces = shallowReactive<
    Record<string, InstanceType<typeof mapboxgl.Marker>>
  >({});

  watch(
    () => [isMapLoaded.value, exploreStore.explorePlaces],
    async () => {
      if (!isMapLoaded.value) return;
      const stopTimer = logger.time(
        "explorePlaces, z: " + exploreStore.currentZoom,
      );
      for (const drawPlace of exploreStore.explorePlaces) {
        if (drawnPlaces[drawPlace.id]) {
          if (!drawPlace.isVisible) {
            drawnPlaces[drawPlace.id]!.remove();
          } else {
            drawnPlaces[drawPlace.id]!.addTo(map.value!);
          }
        } else if (drawPlace.isVisible) {
          const placeMarker = placeMarkerTemplate.cloneNode(
            true,
          ) as typeof placeMarkerTemplate;

          placeMarker.title = drawPlace.fullName;
          placeMarker.style.zIndex = drawPlace.spot_count.toString();

          if (drawPlace.spot_count >= 100) {
            placeMarker.style.width = placeMarker.style.height = "60px";
            placeMarker.style.fontSize = "2.65rem";
            const placeMarkerCount = placeMarker.querySelector("span")!;
            placeMarkerCount.innerHTML = drawPlace.spot_count.toString();
            placeMarkerCount.style.top = "-0.35em";
          } else if (drawPlace.spot_count >= 10) {
            placeMarker.style.width = placeMarker.style.height = "50px";
            placeMarker.style.fontSize = "2.15rem";
            const placeMarkerCount = placeMarker.querySelector("span")!;
            placeMarkerCount.innerHTML = drawPlace.spot_count.toString();
            placeMarkerCount.style.top = "-0.25em";
          } else {
            placeMarker.style.width = placeMarker.style.height = "40px";
            placeMarker.style.fontSize = "1.9rem";
            const placeMarkerCount = placeMarker.querySelector("span")!;
            placeMarkerCount.innerHTML = drawPlace.spot_count.toString();
            placeMarkerCount.style.top = "-0.2em";
          }

          const mapMarker = new mapboxgl.Marker({
            element: placeMarker,
            anchor: "center",
          })
            .setLngLat([drawPlace.lng, drawPlace.lat])
            .addTo(map.value!);

          mapMarker
            .getElement()
            .addEventListener("click", () => showPlace(drawPlace));

          drawnPlaces[drawPlace.id] = mapMarker;
        }
      }
      stopTimer(`places: ${exploreStore.explorePlaces.length}`);
    },
  );

  // drawnSpots[spot.id] = mapMarker;
  const drawnSpots = shallowReactive<
    Record<string, InstanceType<typeof mapboxgl.Marker>>
  >({});

  const MAX_DRAW_COUNT = 25;
  const spotsDrawing = ref<boolean>(false);
  const drawSpots = async () => {
    const stopTimer = logger.time(
      "exploreSpots, z: " + exploreStore.currentZoom,
    );

    spotsDrawing.value = true;
    let drawCount = 0;

    for (const spot of exploreStore.exploreSpots) {
      if (drawnSpots[spot.id]) {
        if (!spot.isVisible) {
          drawnSpots[spot.id]!.remove();
        } else {
          drawnSpots[spot.id]!.addTo(map.value!);
        }
      } else if (spot.isVisible) {
        const spotMarker = spotMarkerTemplate.cloneNode(
          true,
        ) as typeof spotMarkerTemplate;

        spotMarker.title = spot.name!;

        const mapMarker = new mapboxgl.Marker({
          element: spotMarker,
          anchor: "center",
        })
          .setLngLat([spot.lon, spot.lat])
          .addTo(map.value!);

        mapMarker.getElement().addEventListener("click", () => showSpot(spot));
        drawnSpots[spot.id] = mapMarker;

        if (++drawCount % MAX_DRAW_COUNT === 0) {
          await new Promise((res) => requestAnimationFrame(() => res(void 0)));
        }
      }
    }
    spotsDrawing.value = false;
    stopTimer(
      `spots: ${exploreStore.exploreSpots.length}, drawn: ${drawCount}`,
    );
  };

  let drawTimeout: ReturnType<typeof setTimeout>;
  watch(
    () => [isMapLoaded.value, exploreStore.exploreSpots],
    async () => {
      if (!isMapLoaded.value) return;

      await until(() => spotsDrawing.value).toBe(false);
      // at this point in the code, we could have several awaited "threads" waiting to run
      // so add a timeout so only one will run
      clearTimeout(drawTimeout);
      drawTimeout = setTimeout(drawSpots, 1);
    },
  );

  // quick type for new/old value in callback, they match the watch ordering
  type WatchZoomAndPanMapArg = [
    undefined | boolean,
    undefined | null | Place,
    undefined | null | Spot,
  ];
  const zoomAndPanMap = async (
    _newValue: WatchZoomAndPanMapArg,
    [previousIsMapLoaded, previousPlace, previousSpot]: WatchZoomAndPanMapArg,
  ) => {
    if (!isMapLoaded.value) {
      // not yet ready, bail
      return;
    }

    // this could all be triggered by isMapLoaded, so we must set isMapReady before finishing
    // to tell the rest of the code the map is ready for interaction

    if (currentSpot.value) {
      // current spot changed
      if (
        previousIsMapLoaded &&
        previousSpot &&
        currentSpot.value.id === previousSpot.id
      ) {
        // same spot
        return;
      }

      map.value!.setCenter({
        lat: currentSpot.value.lat,
        lng: currentSpot.value.lon,
      });

      if (map.value!.getZoom()! < SPOT_ZOOM) {
        // zoom in, but not out
        map.value!.setZoom(SPOT_ZOOM);
      }

      isMapReady.value = true;
      return;
    }

    if (currentPlace.value) {
      // current place
      if (
        previousIsMapLoaded &&
        previousPlace &&
        currentPlace.value.id === previousPlace.id
      ) {
        // same place
        return;
      }

      // current place changed
      map.value!.flyTo({
        center: {
          lat: currentPlace.value.lat,
          lng: currentPlace.value.lng,
        },
        zoom: currentPlace.value.place ? PLACE_ZOOM : REGION_ZOOM,
        animate: isMapReady.value,
      });

      isMapReady.value = true;
      return;
    }

    // isMapLoaded
    isMapReady.value = true;
  };
  watch([isMapLoaded, currentPlace, currentSpot], zoomAndPanMap);

  return { map, isMapReady };
};
